<!-- Header Navigation (Same as loan-validation, without Stats button) -->
<!-- Page Header Component -->
<app-page-header></app-page-header>

<div class="page-title-bar">
  <div class="page-title-wrap">
    <button
      class="section-back-btn"
      (click)="router.navigate(['/home'])"
      title="Back to Dashboard"
      aria-label="Back to Dashboard">
      <svg
        class="back-arrow-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <path d="M19 12H5"></path>
        <path d="M12 19l-7-7 7-7"></path>
      </svg>
    </button>
    <h1 class="page-title">Analytics</h1>
  </div>
</div>

<div class="stats-dashboard">
  <!-- Loading/Empty State -->
  @if (filteredStats$ | async; as stats) {
    @if (!(isDataLoaded$ | async)) {
      <!-- True initial load: HTTP response not yet arrived -->
      <div class="page-loading-state">
        <div class="docsila-loader">
          <div class="beat-loader">
            <div class="beat"></div>
            <div class="beat"></div>
            <div class="beat"></div>
            <div class="beat"></div>
            <div class="beat"></div>
          </div>
          <p class="loader-text">Loading stats dashboard data...</p>
        </div>
      </div>
    } @else {
      <!-- Data is loaded — always show summary stats and filters -->
      <div class="summary-stats">
        <div class="summary-stat-item stat-models">
          <span class="stat-value">{{ stats.totalModels }}</span>
          <span class="stat-label">Trained Models</span>
        </div>
        <div class="summary-stat-item stat-fields">
          <span class="stat-value">{{ stats.totalFields }}</span>
          <span class="stat-label">Trained Fields</span>
        </div>
        <div class="summary-stat-item stat-files">
          <span class="stat-value">{{ stats.totalFiles }}</span>
          <span class="stat-label">Files Uploaded</span>
        </div>
        <div class="summary-stat-item stat-accuracy">
          <span class="stat-value">{{ (evaluationOutcome$ | async)?.overallAccuracy | number:'1.2-2' }}%</span>
          <span class="stat-label">Overall Accuracy</span>
        </div>
      </div>

      <!-- Models and Accuracy Filters always visible once data is loaded -->
      @if (availableFilters$ | async; as filters) {
        <div class="filters-header">
          <h2>Filters</h2>
          @if (hasActiveFilters$ | async) {
            <button class="btn-reset" (click)="resetFilters()"
              title="Clear all active filters">
              Reset All
            </button>
          }
        </div>
        <div class="filters-container" [class.is-disabled]="stats.totalModels === 0 && stats.totalFields === 0">
          <!-- Models Filter Dropdown (Excel-style) -->
          <div class="filter-dropdown">
            <button class="filter-dropdown-toggle" (click)="toggleModelsDropdown()" [disabled]="stats.totalModels === 0 && stats.totalFields === 0">
              <span class="filter-label">Filter by Models</span>
              @if (getSelectedModelsCount() > 0) {
                <span class="filter-count">({{ getSelectedModelsCount() }})</span>
              }
              <svg class="filter-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline [attr.points]="isModelsDropdownOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline>
              </svg>
            </button>

            @if (isModelsDropdownOpen) {
              <div class="filter-dropdown-panel">
                <!-- Search Box and Action Buttons (Side by Side) -->
                <div class="filter-search-row">
                  <input 
                    type="text" 
                    [(ngModel)]="modelSearchText" 
                    placeholder="Search models..."
                    class="search-input"
                  />
                  <div class="filter-actions-inline">
                    <button class="btn-select-all" (click)="selectAllModels()" title="Select All">All</button>
                    <button class="btn-clear-all" (click)="deselectAllModels()" title="Clear selection" [disabled]="getSelectedModelsCount() === 0 && !modelSearchText">Clear</button>
                  </div>
                </div>

                <!-- Options List -->
                <div class="filter-options-list">
                  @for (model of (availableFilters$ | async)?.models; track model) {
                    @if (!modelSearchText || model.toLowerCase().includes(modelSearchText.toLowerCase())) {
                      <label class="filter-option">
                        <input
                          type="checkbox"
                          [checked]="isModelSelected(model)"
                          (change)="toggleModel(model)"
                        />
                        <span>{{ model }}</span>
                      </label>
                    }
                  }
                </div>
              </div>
            }
          </div>

          <!-- Accuracy Range Filter Dropdown (Excel-style) -->
          <div class="filter-dropdown">
            <button class="filter-dropdown-toggle" (click)="toggleAccuracyDropdown()" [disabled]="stats.totalModels === 0 && stats.totalFields === 0">
              <span class="filter-label">Filter by Accuracy</span>
              @if (getSelectedAccuracyCount() > 0) {
                <span class="filter-count">({{ getSelectedAccuracyCount() }})</span>
              }
              <svg class="filter-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline [attr.points]="isAccuracyDropdownOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline>
              </svg>
            </button>

            @if (isAccuracyDropdownOpen) {
              <div class="filter-dropdown-panel">
                <!-- Action Buttons -->
                <div class="filter-actions">
                  <button class="btn-select-all" (click)="selectAllAccuracyRanges()">Select All</button>
                  <button class="btn-clear-all" (click)="deselectAllAccuracyRanges()" [disabled]="getSelectedAccuracyCount() === 0">Clear All</button>
                </div>

                <!-- Options List -->
                <div class="filter-options-list">
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      [checked]="isAccuracyRangeSelected('0-40')"
                      (change)="toggleAccuracyRange('0-40')"
                    />
                    <span>0% - &lt;40%</span>
                  </label>
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      [checked]="isAccuracyRangeSelected('40-70')"
                      (change)="toggleAccuracyRange('40-70')"
                    />
                    <span>40% - &lt;70%</span>
                  </label>
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      [checked]="isAccuracyRangeSelected('70-80')"
                      (change)="toggleAccuracyRange('70-80')"
                    />
                    <span>70% - &lt;80%</span>
                  </label>
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      [checked]="isAccuracyRangeSelected('80-90')"
                      (change)="toggleAccuracyRange('80-90')"
                    />
                    <span>80% - &lt;90%</span>
                  </label>
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      [checked]="isAccuracyRangeSelected('90-100')"
                      (change)="toggleAccuracyRange('90-100')"
                    />
                    <span>90% - 100%</span>
                  </label>
                </div>
              </div>
            }
          </div>
        </div>
      }

      @if (stats.totalModels === 0 && stats.totalFields === 0) {
        <!-- No data available at all -->
        <div class="no-filter-results no-data-wrapper">
          <p class="no-data-text">No data available</p>
        </div>
      } @else if (stats.totalFields === 0) {
        <!-- Filter active but no matching data -->
        <div class="no-filter-results">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="11" y1="8" x2="11" y2="12"></line>
            <line x1="11" y1="16" x2="11.01" y2="16"></line>
          </svg>
          <p>No data matches the selected filters.</p>
          <span>Try adjusting or clearing your filters.</span>
        </div>
      } @else {
      @if (modelStatusCounts$ | async; as statusCounts) {
        <div class="status-section">
          <h2>Document Model Status</h2>
          <p class="section-description">Ground truth health status of document models</p>
          <div class="status-counts">
            <div class="status-count-item status-healthy">
              <span class="status-count">{{ statusCounts.HEALTHY }}</span>
              <span class="status-label">Healthy</span>
            </div>
            <div class="status-count-item status-field-missing">
              <span class="status-count">{{ statusCounts.FIELD_MISSING }}</span>
              <span class="status-label">Field Missing</span>
            </div>
            <div class="status-count-item status-field-update">
              <span class="status-count">{{ statusCounts.FIELD_UPDATE }}</span>
              <span class="status-label">Field Update</span>
            </div>
            <div class="status-count-item status-no-sample">
              <span class="status-count">{{ statusCounts.NO_SAMPLE }}</span>
              <span class="status-label">No Sample</span>
            </div>
          </div>
        </div>
      }

      <!-- Evaluation Outcome - Only show when model is selected (Full Width) -->
      @if ((hasModelSelected$ | async) && (evaluationOutcome$ | async); as outcome) {
        <div class="outcome-section">
          <h2>Evaluation Outcome</h2>
          <p class="section-description">Aggregated results from latest evaluation runs</p>
          <div class="outcome-cards">
            <div class="outcome-card outcome-success">
              <div class="outcome-value">{{ outcome.successFiles }}</div>
              <div class="outcome-label">Passed Files</div>
              <div class="outcome-detail">of {{ outcome.totalFiles }} total</div>
            </div>
            <div class="outcome-card outcome-failed">
              <div class="outcome-value">{{ outcome.failedFiles }}</div>
              <div class="outcome-label">Failed Files</div>
              <div class="outcome-detail">{{ outcome.passRate | number:'1.0-0' }}% pass rate</div>
            </div>
            <div class="outcome-card outcome-accuracy">
              <div class="outcome-value">{{ outcome.overallAccuracy | number:'1.2-2' }}%</div>
              <div class="outcome-label">Overall Accuracy</div>
              <div class="outcome-detail">average across models</div>
            </div>
            <div class="outcome-card outcome-fields">
              <div class="outcome-value">{{ outcome.matchedFields }}</div>
              <div class="outcome-label">Matched Fields</div>
              <div class="outcome-detail">{{ outcome.unmatchedFields }} unmatched of {{ outcome.totalFields }}</div>
            </div>
          </div>
        </div>
      }

      <!-- Insights Sections (3 Columns Side-by-Side) -->
      @if (insights$ | async; as insights) {
        <div class="insights-container">
          <!-- Top 10 Most Accurate Files -->
          <div class="insights-section">
            <div class="insights-header">
              <h3>Most Accurate Files</h3>
              <button class="btn-toggle-collapse" (click)="toggleTopFilesInsights()" [title]="isTopFilesCollapsed ? 'Expand' : 'Collapse'">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  @if (isTopFilesCollapsed) {
                    <polyline points="9 18 15 12 9 6"></polyline>
                  } @else {
                    <polyline points="6 9 12 15 18 9"></polyline>
                  }
                </svg>
              </button>
            </div>
            <p class="insights-limit-note">Showing up to 10 files</p>
            @if (!isTopFilesCollapsed) {
              <ul class="insights-list">
              @for (item of insights.topAccurate; track item.fileName) {
                <li class="insight-item">
                  <div class="insight-info">
                    <span class="insight-name" [title]="'File: ' + getFileName(item.fileName) + ' | Model: ' + item.model + ' | Accuracy: ' + (item.accuracy | number:'1.2-2') + '%'">
                      {{ getFileName(item.fileName) }}
                    </span>
                    <span class="insight-model">{{ item.model }}</span>
                  </div>
                  <span
                    [class]="'insight-accuracy ' + getAccuracyClass(item.accuracy)"
                  >
                    {{ item.accuracy | number:'1.2-2' }}%
                  </span>
                </li>
              }
              @empty {
                <li class="no-insights">No data available</li>
              }
              </ul>
            }
          </div>

          <!-- Top 10 Least Accurate Files -->
          <div class="insights-section">
            <div class="insights-header">
              <h3>Least Accurate Files</h3>
              <button class="btn-toggle-collapse" (click)="toggleLeastFilesInsights()" [title]="isLeastFilesCollapsed ? 'Expand' : 'Collapse'">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  @if (isLeastFilesCollapsed) {
                    <polyline points="9 18 15 12 9 6"></polyline>
                  } @else {
                    <polyline points="6 9 12 15 18 9"></polyline>
                  }
                </svg>
              </button>
            </div>
            <p class="insights-limit-note">Showing up to 10 files</p>
            @if (!isLeastFilesCollapsed) {
              <ul class="insights-list">
              @for (item of insights.leastAccurate; track item.fileName) {
                <li class="insight-item">
                  <div class="insight-info">
                    <span class="insight-name" [title]="'Click to navigate to ground truth page for ' + getFileName(item.fileName) + ' (' + item.model + ')'"
                          (click)="navigateToLeastAccurateFile(item.fileName, item.model, item.fileId)">
                      <a class="insight-link">{{ getFileName(item.fileName) }}</a>
                    </span>
                    <span class="insight-model">{{ item.model }}</span>
                  </div>
                  <span
                    [class]="'insight-accuracy ' + getAccuracyClass(item.accuracy)"
                  >
                    {{ item.accuracy | number:'1.2-2' }}%
                  </span>
                </li>
              }
              @empty {
                <li class="no-insights">No data available</li>
              }
              </ul>
            }
          </div>

          <!-- Top 10 Least Accurate Fields -->
          <div class="insights-section">
            <div class="insights-header">
              <h3>Least Accurate Fields</h3>
              <button class="btn-toggle-collapse" (click)="toggleLeastFieldsInsights()" [title]="isLeastFieldsCollapsed ? 'Expand' : 'Collapse'">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  @if (isLeastFieldsCollapsed) {
                    <polyline points="9 18 15 12 9 6"></polyline>
                  } @else {
                    <polyline points="6 9 12 15 18 9"></polyline>
                  }
                </svg>
              </button>
            </div>
            <p class="insights-limit-note">Showing up to 10 fields</p>
            @if (!isLeastFieldsCollapsed) {
              <ul class="insights-list">
              @for (item of insights.leastAccurateFields; track item.fieldName + item.model) {
                <li class="insight-item">
                  <div class="insight-info">
                    <span class="insight-name" [title]="'Click to navigate to prompt tuning page for ' + item.fieldName + ' (' + item.model + ')'"
                          (click)="navigateToLeastAccurateField(item.fieldName, item.model)">
                      <a class="insight-link">{{ item.fieldName }}</a>
                    </span>
                    <span class="insight-model">{{ item.model }}</span>
                  </div>
                  <span
                    [class]="'insight-accuracy ' + getAccuracyClass(item.accuracy)"
                  >
                    {{ item.accuracy | number:'1.2-2' }}%
                  </span>
                </li>
              }
              @empty {
                <li class="no-insights">No data available</li>
              }
              </ul>
            }
          </div>
        </div>
      }
    }
  }
} @else {
  <div class="loading-state">
    <div class="spinner"></div>
    <p>Loading dashboard data...</p>
  </div>
}
</div>

